<!-- ABOUTME: Parent component for broadcast email creation and editing -->
<!-- ABOUTME: Orchestrates filters, user selection, and email composition without page reloads -->
<template>
  <div class="max-w-7xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 mb-2">
        {{ emailContentId ? 'Edit' : 'Send' }} Broadcast Email
      </h1>
      <p class="text-gray-600">Create multi-language email to send to selected users</p>
    </div>

    <!-- Main Layout: Sidebar + Content (NO GRID!) -->
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- LEFT SIDEBAR: FILTERS AND USER LIST -->
      <div class="w-full lg:w-80 lg:flex-shrink-0 space-y-4">
        <FilterPanel
          :current-filters="filters"
          @filters-changed="handleFiltersChanged"
        />

        <UserSelectionPanel
          :users="users"
          :selected-user-ids="selectedUserIds"
          :is-loading="isLoadingUsers"
          @selection-changed="handleSelectionChanged"
        />
      </div>

      <!-- RIGHT SIDE: EMAIL COMPOSITION -->
      <div class="flex-1 min-w-0 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <!-- Error Display -->
        <div v-if="errors.length > 0" class="mb-6 bg-red-50 border-l-4 border-red-500 rounded-md p-4">
          <h4 class="text-sm font-semibold text-red-800 mb-2">
            {{ errors.length }} error(s) prohibited this email from being sent:
          </h4>
          <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
            <li v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
          </ul>
        </div>

        <!-- Email Title -->
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Email Campaign Title
          </label>
          <input
            type="text"
            v-model="emailTitle"
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 transition-colors focus:border-primary focus:outline-none focus:ring-primary"
            placeholder="Internal name (e.g., &quot;Maintenance Announcement Jan 2025&quot;)">
        </div>

        <!-- Multi-language Email Editor -->
        <MultiLanguageEmailForm
          ref="multiLangEditor"
          :email-content-id="emailContentId"
          :initial-variants="initialVariants"
          :templates="templates"
          :show-template-selector="true"
          :save-url="saveUrl"
          email-type="broadcast"
          @saved="handleEmailSaved"
          @error="handleEmailError"
        />

        <!-- Form Actions -->
        <div class="mt-6 flex flex-col gap-4 pt-6 border-t border-gray-200">
          <!-- Preview Status Message -->
          <div v-if="multiLangEditor?.previewMessage" class="text-sm" :class="{
            'text-green-500': multiLangEditor?.previewMessageClass === 'preview-success',
            'text-red-500': multiLangEditor?.previewMessageClass === 'preview-error',
            'text-amber-500': multiLangEditor?.previewMessageClass === 'preview-pending'
          }">
            {{ multiLangEditor.previewMessage }}
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-3 flex-wrap">
            <button
              @click="sendPreview"
              :disabled="!canSendPreview || multiLangEditor?.previewLoading"
              type="button"
              class="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              :title="canSendPreview ? 'Send test email to yourself' : 'Fill in subject and body to send preview'">
              {{ multiLangEditor?.previewLoading ? 'Sending Preview...' : 'Send Preview' }}
            </button>

            <button
              @click="saveDraft"
              :disabled="isSaving"
              type="button"
              class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors disabled:opacity-50 cursor-pointer">
              {{ isSaving ? 'Saving...' : 'Save Draft' }}
            </button>

            <button
              @click="sendEmail"
              :disabled="isSaving || selectedUserIds.length === 0"
              type="button"
              class="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-dark focus:outline-none focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
              {{ isSaving ? 'Sending...' : 'Send Email' }}
            </button>

            <a
              :href="cancelUrl"
              class="px-4 py-2 text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors cursor-pointer">
              Cancel
            </a>

            <div v-if="selectedUserIds.length === 0" class="ml-auto text-xs text-amber-600">
              ⚠ No recipients selected
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 shadow-xl">
        <div class="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p class="text-sm text-gray-700">{{ loadingMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import FilterPanel from './FilterPanel.vue'
import UserSelectionPanel from './UserSelectionPanel.vue'
import MultiLanguageEmailForm from './MultiLanguageEmailForm.vue'

const props = defineProps({
  emailContentId: {
    type: [String, Number],
    default: null
  },
  initialFilters: {
    type: Object,
    default: () => ({})
  },
  initialUsers: {
    type: Array,
    default: () => []
  },
  initialVariants: {
    type: Object,
    default: () => ({})
  },
  initialTitle: {
    type: String,
    default: ''
  },
  templates: {
    type: Array,
    default: () => []
  },
  saveUrl: {
    type: String,
    default: '/admin/broadcast_emails'
  },
  filterUrl: {
    type: String,
    default: '/admin/broadcast_emails/filter_users'
  },
  cancelUrl: {
    type: String,
    default: '/admin/broadcast_emails'
  }
})

// State
const filters = ref({ ...props.initialFilters })
const users = ref([...props.initialUsers])
const selectedUserIds = ref([])
const emailTitle = ref(props.initialTitle)
const errors = ref([])
const isLoading = ref(false)
const isLoadingUsers = ref(false)
const isSaving = ref(false)
const loadingMessage = ref('Loading...')
const multiLangEditor = ref(null)

onMounted(() => {
  // Format users for display
  users.value = props.initialUsers.map(user => ({
    id: user.id,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name',
    email: user.email
  }))
})

// Computed property to check if preview can be sent
const canSendPreview = computed(() => {
  return multiLangEditor.value?.canSendPreview || false
})

// Get CSRF token
function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}

// Handle filter changes
async function handleFiltersChanged(newFilters) {
  filters.value = { ...newFilters }
  isLoadingUsers.value = true
  errors.value = []

  try {
    const params = new URLSearchParams()
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) params.append(`filters[${key}]`, value)
    })

    const response = await axios.get(`${props.filterUrl}?${params.toString()}`, {
      headers: {
        'X-CSRF-Token': getCsrfToken()
      }
    })

    if (response.data.users) {
      users.value = response.data.users.map(user => ({
        id: user.id,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name',
        email: user.email
      }))
    }
  } catch (error) {
    console.error('Failed to filter users:', error)
    errors.value = [`Failed to load filtered users: ${error.message}`]
  } finally {
    isLoadingUsers.value = false
  }
}

// Handle user selection changes
function handleSelectionChanged(newSelectedIds) {
  selectedUserIds.value = [...newSelectedIds]
  errors.value = errors.value.filter(e => !e.includes('recipient'))
}

// Handle email content saved
function handleEmailSaved(newEmailContentId) {
  console.log('Email content saved:', newEmailContentId)
}

// Handle email content error
function handleEmailError(error) {
  errors.value = [error]
}

// Send preview email
function sendPreview() {
  if (multiLangEditor.value && multiLangEditor.value.sendPreview) {
    multiLangEditor.value.sendPreview()
  }
}

// Save draft
async function saveDraft() {
  errors.value = []

  // Validate title
  if (!emailTitle.value || !emailTitle.value.trim()) {
    errors.value = ['Email campaign title is required']
    return
  }

  // Validate email content
  if (multiLangEditor.value && !multiLangEditor.value.validate()) {
    errors.value = ['Please fill in at least one language variant']
    return
  }

  // Get all variants from child component
  const allVariants = multiLangEditor.value ? multiLangEditor.value.getAllVariants() : {}

  // Transform variants to Rails nested attributes format
  const variants_attributes = Object.entries(allVariants)
    .filter(([locale, data]) => {
      // Only include variants that have actual content
      return data.subject && data.subject.trim() && data.body && data.body.trim()
    })
    .map(([locale, data]) => {
      const attr = {
        locale: locale,
        subject: data.subject,
        body: data.body
      }

      // Only include id if it exists (for existing variants)
      if (data.id) {
        attr.id = data.id
      }

      return attr
    })

  isSaving.value = true

  // Build URL - must start with / for absolute path from root
  const url = props.emailContentId
    ? `${props.saveUrl}/${props.emailContentId}`
    : props.saveUrl

  try {
    const method = props.emailContentId ? 'patch' : 'post'

    console.log(`Saving draft: ${method.toUpperCase()} ${url}`)
    console.log('Variants to save:', variants_attributes)

    const response = await axios[method](url, {
      email_content: {
        title: emailTitle.value,
        variants_attributes: variants_attributes
      },
      save_draft: true
    }, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (response.data.success || response.data.redirect_url) {
      window.location.href = response.data.redirect_url || props.cancelUrl
    } else {
      errors.value = response.data.errors || ['Failed to save draft']
    }
  } catch (error) {
    console.error('Save draft error:', error)
    if (error.response?.status === 404) {
      errors.value = [`Save failed: URL not found (${url}). Please contact support.`]
    } else if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else if (error.response?.data?.error) {
      errors.value = [error.response.data.error]
    } else {
      errors.value = [`Failed to save draft: ${error.message || 'Unknown error'}`]
    }
  } finally {
    isSaving.value = false
  }
}

// Send email
async function sendEmail() {
  errors.value = []

  // Validate recipients
  if (selectedUserIds.value.length === 0) {
    errors.value = ['Please select at least one recipient']
    return
  }

  // Validate title
  if (!emailTitle.value || !emailTitle.value.trim()) {
    errors.value = ['Email campaign title is required']
    return
  }

  // Validate email content
  if (multiLangEditor.value && !multiLangEditor.value.validate()) {
    errors.value = ['Please fill in at least one language variant']
    return
  }

  // Confirm send
  if (!confirm(`Are you sure you want to send this email to ${selectedUserIds.value.length} user(s)?`)) {
    return
  }

  // Get all variants from child component
  const allVariants = multiLangEditor.value ? multiLangEditor.value.getAllVariants() : {}

  // Transform variants to Rails nested attributes format
  const variants_attributes = Object.entries(allVariants)
    .filter(([locale, data]) => {
      // Only include variants that have actual content
      return data.subject && data.subject.trim() && data.body && data.body.trim()
    })
    .map(([locale, data]) => {
      const attr = {
        locale: locale,
        subject: data.subject,
        body: data.body
      }

      // Only include id if it exists (for existing variants)
      if (data.id) {
        attr.id = data.id
      }

      return attr
    })

  isSaving.value = true
  loadingMessage.value = 'Sending email...'

  try {
    const url = props.emailContentId
      ? `${props.saveUrl}/${props.emailContentId}`
      : props.saveUrl

    const method = props.emailContentId ? 'patch' : 'post'

    const response = await axios[method](url, {
      email_content: {
        title: emailTitle.value,
        variants_attributes: variants_attributes
      },
      user_ids: selectedUserIds.value
    }, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (response.data.success || response.data.redirect_url) {
      window.location.href = response.data.redirect_url || props.cancelUrl
    } else {
      errors.value = response.data.errors || ['Failed to send email']
    }
  } catch (error) {
    console.error('Send email error:', error)
    errors.value = [error.response?.data?.error || 'Failed to send email']
  } finally {
    isSaving.value = false
  }
}
</script>
